import { motion } from 'framer-motion';
import {
  Book,
  HelpCircle,
  Mail,
  MessageCircle,
  Phone,
  Send,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const supportChannels = [
  {
    icon: Mail,
    title: 'Email Support',
    description: "Send us a detailed message and we'll get back to you.",
    contact: 'support@documint.id',
    response: 'Within 24 hours',
    href: 'mailto:support@documint.id',
  },
  {
    icon: Phone,
    title: 'Phone Support',
    description: 'Talk to our team directly for urgent issues.',
    contact: '+91-9945101015',
    response: 'Mon-Fri, 9AM-6PM IST',
    href: 'tel:+919945101015',
  },
];

const quickLinks = [
  {
    icon: Book,
    title: 'Documentation',
    description: 'Guides and API references for building with Documint.',
    path: '/docs',
  },
  {
    icon: HelpCircle,
    title: 'FAQ',
    description: 'Answers to the questions we hear most often.',
    path: '/faq',
  },
  {
    icon: MessageCircle,
    title: 'Contact Sales',
    description: 'Talk to our team about pricing or a custom plan.',
    path: '/contact',
  },
];

const Support = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-100 via-surface-200 to-surface-100">
      {/* Hero Section */}
      <section className="pt-24 pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="relative">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                How Can We
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-neon-400">
                  {' '}
                  Help?
                </span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-400 to-neon-400 rounded-full mx-auto mb-6"></div>
            </div>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Reach our support team directly, or browse our self-serve
              resources below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="pb-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
            {supportChannels.map((channel, index) => (
              <motion.a
                key={channel.title}
                href={channel.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-6 md:p-8 flex items-start space-x-4 hover:bg-white/5 transition-colors duration-200"
              >
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <channel.icon className="w-6 h-6 text-primary-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {channel.title}
                  </h3>
                  <p className="text-gray-300 mb-2">{channel.description}</p>
                  <p className="text-primary-400 font-medium">
                    {channel.contact}
                  </p>
                  <p className="text-sm text-gray-400">
                    Response time: {channel.response}
                  </p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="pb-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-white mb-6 text-center"
            >
              Self-Serve Resources
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-6">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className="glass-card p-6 h-full flex flex-col hover:bg-white/5 transition-colors duration-200"
                  >
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mb-4">
                      <link.icon className="w-6 h-6 text-primary-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {link.title}
                    </h3>
                    <p className="text-gray-300">{link.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="glass-card max-w-2xl mx-auto p-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Still Stuck?
              </h2>
              <p className="text-gray-300 mb-6">
                Email our support team directly and we&apos;ll follow up within
                24 hours.
              </p>
              <motion.a
                href="mailto:support@documint.id"
                className="btn-primary inline-flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Email Support</span>
                <Send size={18} />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Support;
